import { Component } from '@theme/component';

const PARALLAX_SELECTOR = '.rhm-parallax';
const DEFAULT_PARALLAX_SHIFT = 0.18;

/**
 * Drives the About page as a horizontal scroll: the section pins and vertical
 * scroll distance is translated into leftward movement of the track.
 *
 * Elements carrying `.rhm-parallax` drift further left than the track itself.
 * They all share one config, so opting an element in or out is purely a matter
 * of adding or removing the class in the Liquid.
 *
 * When GSAP is unavailable or the visitor prefers reduced motion, nothing is
 * pinned and the stylesheet's native horizontal scroll stays in force.
 */
class RhmAboutScrollComponent extends Component {
  #ctx: any = null;
  #alive = false;

  connectedCallback() {
    super.connectedCallback();
    if (this.#alive) return;
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    queueMicrotask(() => {
      if (this.isConnected) return;
      this.#cleanup();
    });
  }

  #cleanup() {
    this.#alive = false;
    this.classList.remove('is-pinned');

    const ST = (window as any).ScrollTrigger;
    if (ST) {
      ST.getAll().forEach((st: any) => {
        if (this.contains(st.trigger) || st.trigger === this) {
          st.kill(false);
        }
      });
    }

    this.#ctx?.revert();
    this.#ctx = null;
  }

  /** Native scroll fallback needs an explicit tab stop in Safari. */
  #enableNativeScroll(viewport: HTMLElement) {
    viewport.tabIndex = 0;
  }

  #headerOffset(): number {
    const raw = getComputedStyle(document.body).getPropertyValue('--header-height');
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  #init() {
    const viewport = this.refs.viewport as HTMLElement | undefined;
    const track = this.refs.track as HTMLElement | undefined;
    if (!viewport || !track) return;

    const g = (window as any).gsap;
    const ST = (window as any).ScrollTrigger;

    if (!g || !ST || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#enableNativeScroll(viewport);
      return;
    }

    this.#alive = true;
    this.classList.add('is-pinned');

    const shift = parseFloat(this.dataset.parallaxShift || '') || DEFAULT_PARALLAX_SHIFT;

    this.#ctx = g.context(() => {
      const distance = () => Math.max(0, track.scrollWidth - viewport.clientWidth);

      // Pin when the section docks under the sticky header — not at the
      // viewport top. `start: 'top top'` would force a header-height of
      // vertical scroll before the horizontal tween starts.
      const scrollTween = g.to(track, {
        x: () => -distance(),
        ease: 'none',
        scrollTrigger: {
          trigger: this,
          pin: true,
          scrub: 1,
          start: () => `top ${this.#headerOffset()}px`,
          end: () => `+=${distance()}`,
          invalidateOnRefresh: true,
          anticipatePin: 1,
        },
      });

      // Triggers on elements inside a horizontally moving track have to be told
      // which animation is doing the moving, otherwise their start/end never fire.
      g.utils.toArray(PARALLAX_SELECTOR, this).forEach((el: HTMLElement) => {
        g.to(el, {
          x: () => -(window.innerWidth * shift),
          ease: 'none',
          scrollTrigger: {
            trigger: el,
            containerAnimation: scrollTween,
            start: 'left right',
            end: 'right left',
            scrub: true,
            invalidateOnRefresh: true,
          },
        });
      });

      // Flow panels size from content: fonts change copy metrics (and thus
      // track width). Aspect-ratio on figures reserves media width before
      // images decode, so no image-load refresh (avoids mid-scroll jumps).
      document.fonts?.ready.then(() => ST.refresh());
    }, this);
  }
}

if (!customElements.get('rhm-about-scroll')) {
  customElements.define('rhm-about-scroll', RhmAboutScrollComponent);
}
